import React, { useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { Search, FileText, Printer, Download, Baby, X, Trash2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const MaternityFiles: React.FC = () => {
  const {
    maternityFiles,
    deleteMaternityFile,
    currentUser
  } = useData();
  const isAdmin = currentUser?.role === 'Administrator';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<MaternityFile | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const handleDeleteFile = (file: MaternityFile) => {
    if (window.confirm(`WARNING: This will permanently delete the maternity file for ${file.patient.firstName} ${file.patient.surname} (${file.bmcFileNumber}) and ALL of its records. This cannot be undone. Are you sure?`)) {
      deleteMaternityFile(file.id);
      setSelectedFile(null);
    }
  };
  const filteredFiles = maternityFiles.filter(m => {
    const q = searchQuery.toLowerCase();
    return m.patient.firstName.toLowerCase().includes(q) || m.patient.surname.toLowerCase().includes(q) || m.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const handlePrint = () => {
    document.body.classList.add('printing-maternity-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-maternity-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedFile) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_Maternity_${selectedFile.bmcFileNumber}_${selectedFile.patient.firstName}_${selectedFile.patient.surname}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="space-y-6" data-id="element-3344" data-mp="25vvo1bq4in5e">
      <div className="flex justify-between items-center print:hidden" data-id="element-3345" data-mp="20zoqo7bsmjz2">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3346" data-mp="20xc0x7zsn5bg">
          Maternity Patient Files
        </h1>
      </div>

      {!selectedFile ? <Card noPadding data-id="element-3347" data-mp="29ysbbfqlz7fo">
          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-3348" data-mp="29ya45o2er8sz">
            <div className="relative max-w-md" data-id="element-3349" data-mp="2ony68rieybhr">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3350" data-mp="2oqu3seqcmy5o">
                <Search className="h-5 w-5 text-gray-400" data-id="element-3351" data-mp="2js0rgpmstd73" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" placeholder="Search by name or BMC file number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3352" data-mp="2xommztz6ypyc" />
            </div>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3353" data-mp="t9kfyxhiaslu">
            {filteredFiles.length > 0 ? filteredFiles.map(file => <li key={file.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-3354" data-mp="kemidd3eyro6">
                  <div className="flex items-center" data-id="element-3355" data-mp="5jzvga5pfnag">
                    <div className="bg-pink-100 p-2 rounded-full mr-4" data-id="element-3356" data-mp="ah8d89mxxpb6">
                      <Baby className="h-6 w-6 text-pink-600" data-id="element-3357" data-mp="3smuv6wnp9zir" />
                    </div>
                    <div data-id="element-3358" data-mp="1gaarvs5hpup">
                      <p className="text-sm font-medium text-blue-600" data-id="element-3359" data-mp="1fid8fezti1s">
                        {file.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-3360" data-mp="1qvzw9vy75k7t">
                        {file.patient.firstName} {file.patient.surname}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3361" data-mp="1tgpixmkac33g">
                        Registered:{' '}
                        {new Date(file.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-3362" data-mp="1kgt2g881uajh">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${file.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-3363" data-mp="1fh6m3pwxce6g">
                      {file.stage === 'COMPLETED' ? '✓ Completed' : 'Active (Nursing)'}
                    </span>
                    <Button onClick={() => setSelectedFile(file)} data-id="element-3364" data-mp="1ydps46cfllys">
                      <FileText className="w-4 h-4 mr-2" data-id="element-3365" data-mp="2vtv7c60sdbnt" /> View File
                    </Button>
                    {isAdmin && <Button variant="danger" onClick={() => handleDeleteFile(file)} data-id="element-3366" data-mp="2r2dfc7gqqlez">
                        <Trash2 className="w-4 h-4 mr-2" data-id="element-3367" data-mp="33do0zmmf0au2" /> Delete
                      </Button>}
                  </div>
                </li>) : <li className="p-12 text-center text-gray-500" data-id="element-3368" data-mp="3dbtzdu90iyu6">
                <Baby className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-3369" data-mp="3ddesrgjlcbqg" />
                <p className="text-lg font-medium text-gray-900" data-id="element-3370" data-mp="3he7187kn2clo">
                  No maternity files found
                </p>
              </li>}
          </ul>
        </Card> : <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-3371" data-mp="3r49v09zl3ul2">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible maternity-modal-print" data-id="element-3372" data-mp="3w1js8bt7tajg">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-3373" data-mp="8v9aoeq5vb61">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3374" data-mp="q7qlzq9eop3t">
                Maternity File — {selectedFile.bmcFileNumber}
              </h3>
              <div className="flex items-center space-x-2" data-id="element-3375" data-mp="144nlsh7i5gwu">
                <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-3376" data-mp="z8ouakqvnis7">
                  <Printer className="w-4 h-4 mr-1" data-id="element-3377" data-mp="bhjfm8woawjh" /> Print
                </Button>
                <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-3378" data-mp="6izntrveeqj7">
                  <Download className="w-4 h-4 mr-1" data-id="element-3379" data-mp="gh5o651k3oly" />{' '}
                  {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
                {isAdmin && <Button variant="danger" size="sm" onClick={() => handleDeleteFile(selectedFile)} data-id="element-3380" data-mp="3oumh9r7d1xik">
                    <Trash2 className="w-4 h-4 mr-1" data-id="element-3381" data-mp="1zeiqd14wz5eh" /> Delete
                  </Button>}
                <button onClick={() => setSelectedFile(null)} className="p-1.5 hover:bg-gray-100 rounded" data-id="element-3382" data-mp="248n3610v27nh">
                  <X className="w-5 h-5 text-gray-500" data-id="element-3383" data-mp="1uhsdsnsbirxt" />
                </button>
              </div>
            </div>

            <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-3384" data-mp="2i6usbmrno4o3">
              {/* Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-4" data-id="element-3385" data-mp="2966ht21h0mfy">
                <div className="flex items-center" data-id="element-3386" data-mp="2e485y1dr7bya">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-3387" data-mp="2e6ugm9vxy7kq" />
                  <div data-id="element-3388" data-mp="a4vzbll9huy4">
                    <h1 className="text-xl font-bold uppercase" data-id="element-3389" data-mp="a6p6d1zseh6n">
                      BMED MEDICAL CENTRE
                    </h1>
                    <p className="text-xs italic" data-id="element-3390" data-mp="e93o1irpzawc">Caring Healing Leading</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-3391" data-mp="5epaplqr2afv">
                  <h2 className="text-lg font-bold uppercase tracking-widest border-b border-black pb-1 mb-2" data-id="element-3392" data-mp="5de99sks1erx">
                    Maternity Record
                  </h2>
                  <p data-id="element-3393" data-mp="3rmf5p1tttxau">
                    <span className="font-semibold" data-id="element-3394" data-mp="3rmyloczx2bm8">File No:</span>{' '}
                    {selectedFile.bmcFileNumber}
                  </p>
                  <p data-id="element-3395" data-mp="3doh4yz8fhzdt">
                    <span className="font-semibold" data-id="element-3396" data-mp="3ikxqrms5o1nq">Registered:</span>{' '}
                    {new Date(selectedFile.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* PATIENT'S DETAILS */}
              <div className="mb-6" data-id="element-3397" data-mp="1gtceflcu3gnv">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3398" data-mp="1qxsry82ucloi">
                  PATIENT'S DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-3399" data-mp="1lzhgjswwe346">
                  <div data-id="element-3400" data-mp="1zq30lky38j3n">
                    <span className="font-semibold" data-id="element-3401" data-mp="1zqmhcyc051g3">First Name:</span>{' '}
                    {selectedFile.patient.firstName}
                  </div>
                  <div data-id="element-3402" data-mp="1ux9wmzag1esm">
                    <span className="font-semibold" data-id="element-3403" data-mp="29oaf7x4wmp2h">Surname:</span>{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3404" data-mp="24oea1ukh0rlc">
                    <span className="font-semibold" data-id="element-3405" data-mp="24oxqt7ydx9xs">Other Names:</span>{' '}
                    {selectedFile.patient.otherNames}
                  </div>
                  <div data-id="element-3406" data-mp="2en3tvsvxgm48">
                    <span className="font-semibold" data-id="element-3407" data-mp="2eoomhcyoluzg">Age:</span>{' '}
                    {selectedFile.patient.age}
                  </div>
                  <div data-id="element-3408" data-mp="ocr2lqp0eupe">
                    <span className="font-semibold" data-id="element-3409" data-mp="k9ug3owcmeg6">Religion:</span>{' '}
                    {selectedFile.patient.religion}
                  </div>
                  <div data-id="element-3410" data-mp="2jfxaa1ncn542">
                    <span className="font-semibold" data-id="element-3411" data-mp="2jggphalm7fee">Church Attended:</span>{' '}
                    {selectedFile.patient.churchAttended}
                  </div>
                  <div className="col-span-2" data-id="element-3412" data-mp="2tap7h4d0rpyb">
                    <span className="font-semibold" data-id="element-3413" data-mp="2yb3h2qu9r7yg">Residential Address:</span>{' '}
                    {selectedFile.patient.residentialAddress}
                  </div>
                  <div className="col-span-2" data-id="element-3414" data-mp="32avt5divfw8k">
                    <span className="font-semibold" data-id="element-3415" data-mp="37bjky1exch3u">Location/Landmark:</span>{' '}
                    {selectedFile.patient.locationLandmark}
                  </div>
                </div>
              </div>

              {/* SPOUSE DETAILS */}
              <div className="mb-6" data-id="element-3416" data-mp="3h131h0f5zyvj">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3417" data-mp="3m17jiuvq11nv">
                  SPOUSE DETAILS (OPTIONAL)
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-3418" data-mp="3q3kohh2n8vjl">
                  <div data-id="element-3419" data-mp="1eexrik835zsn">
                    <span className="font-semibold" data-id="element-3420" data-mp="1eg8skca5ctg2">First Name:</span>{' '}
                    {selectedFile.spouse.firstName}
                  </div>
                  <div data-id="element-3421" data-mp="1afyox69tuf4d">
                    <span className="font-semibold" data-id="element-3422" data-mp="15e9dvqmlu1ga">Surname:</span>{' '}
                    {selectedFile.spouse.surname}
                  </div>
                  <div data-id="element-3423" data-mp="10gzebi5i49eq">
                    <span className="font-semibold" data-id="element-3424" data-mp="10hr9irv6s54h">Other Names:</span>{' '}
                    {selectedFile.spouse.otherNames}
                  </div>
                  <div data-id="element-3425" data-mp="1t54xw1ktjscz">
                    <span className="font-semibold" data-id="element-3426" data-mp="1t4loxad5ceb3">Occupation:</span>{' '}
                    {selectedFile.spouse.occupation}
                  </div>
                  <div className="col-span-2" data-id="element-3427" data-mp="1jesgujc7qhbl">
                    <span className="font-semibold" data-id="element-3428" data-mp="1je0jp42ywbj7">Contact:</span>{' '}
                    {selectedFile.spouse.contact}
                  </div>
                </div>
              </div>

              {/* ADMISSION */}
              <div className="mb-6" data-id="element-3429" data-mp="2c0v5c4bitgy9">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3430" data-mp="1wketyd6mns6k">
                  ADMISSION
                </h3>
                <div className="flex space-x-16" data-id="element-3431" data-mp="20nkxve69p79v">
                  <div data-id="element-3432" data-mp="20k6zsi4mzk88">
                    <span className="font-semibold" data-id="element-3433" data-mp="1mna6ma11pqo6">Date:</span>{' '}
                    {selectedFile.admission.date}
                  </div>
                  <div data-id="element-3434" data-mp="1rmdc1vkxtywo">
                    <span className="font-semibold" data-id="element-3435" data-mp="1rnohs0xy1gqf">Time of Admission:</span>{' '}
                    {selectedFile.admission.time}
                  </div>
                </div>
              </div>

              {/* GENERAL EXAMINATION */}
              <div className="mb-6" data-id="element-3436" data-mp="25meg1jwi1mgd">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3437" data-mp="25lcqkaeqkxf4">
                  GENERAL EXAMINATION
                </h3>
                <div className="space-y-2" data-id="element-3438" data-mp="2aid0wximcmc1">
                  <div data-id="element-3439" data-mp="2y98w84ma9zo1">
                    <span className="font-semibold" data-id="element-3440" data-mp="2y8z5getezoha">
                      Cardiovascular System:
                    </span>{' '}
                    {selectedFile.generalExam.cardiovascularSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-3441" data-mp="rjorfrmlxfnn">
                    <div data-id="element-3442" data-mp="mlvpjasc77t6">
                      <span className="font-semibold" data-id="element-3443" data-mp="mmomxl5eokyn">Temp:</span>{' '}
                      {selectedFile.generalExam.temp}
                    </div>
                    <div data-id="element-3444" data-mp="wc86yu4uukv3">
                      <span className="font-semibold" data-id="element-3445" data-mp="we1e0ajdr73m">Pulse:</span>{' '}
                      {selectedFile.generalExam.pulse}
                    </div>
                    <div data-id="element-3446" data-mp="3o2lu69w2tuq">
                      <span className="font-semibold" data-id="element-3447" data-mp="3ouhti7eetlj">Respiration:</span>{' '}
                      {selectedFile.generalExam.respiration}
                    </div>
                    <div data-id="element-3448" data-mp="hlhgpqiwvtu3">
                      <span className="font-semibold" data-id="element-3449" data-mp="co9568s9qo1t">BP:</span>{' '}
                      {selectedFile.generalExam.bp}
                    </div>
                  </div>
                  <div className="flex space-x-16" data-id="element-3450" data-mp="1t4twtfxp7d93">
                    <div data-id="element-3451" data-mp="1o34hvp7cuffq">
                      <span className="font-semibold" data-id="element-3452" data-mp="1o3nxv0dg2tr4">Oedema:</span>{' '}
                      {selectedFile.generalExam.oedema}
                    </div>
                    <div data-id="element-3453" data-mp="qotaonjaqhbs">
                      <span className="font-semibold" data-id="element-3454" data-mp="qo1epblsehkz">Anemia:</span>{' '}
                      {selectedFile.generalExam.anemia}
                    </div>
                  </div>
                  <div data-id="element-3455" data-mp="zp7zhwvgyek1">
                    <span className="font-semibold" data-id="element-3456" data-mp="zpr8go355slx">Respiratory System:</span>{' '}
                    {selectedFile.generalExam.respiratorySystem}
                  </div>
                  <div data-id="element-3457" data-mp="70tx74xckw2o">
                    <span className="font-semibold" data-id="element-3458" data-mp="7252582j49vd">
                      Central Nervous System:
                    </span>{' '}
                    {selectedFile.generalExam.centralNervousSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-3459" data-mp="gthwc6bsvc4k">
                    <div data-id="element-3460" data-mp="3fgdpo1q72ago">
                      <span className="font-semibold" data-id="element-3461" data-mp="3fgvrrezjfiwi">Breasts:</span>{' '}
                      {selectedFile.generalExam.breasts}
                    </div>
                    <div data-id="element-3462" data-mp="3cxinxpfdnjp4">
                      <span className="font-semibold" data-id="element-3463" data-mp="3cvo4kfjr7zn5">Varicosities:</span>{' '}
                      {selectedFile.generalExam.varicosities}
                    </div>
                    <div data-id="element-3464" data-mp="32y9zfc3a6xa5">
                      <span className="font-semibold" data-id="element-3465" data-mp="32xrr3h3cgslv">Vaginal Discharge:</span>{' '}
                      {selectedFile.generalExam.vaginalDischarge}
                    </div>
                  </div>
                  <div data-id="element-3466" data-mp="2tz6cty4wnzqw">
                    <span className="font-semibold" data-id="element-3467" data-mp="2tzfu8xbvwykz">Other Abnormalities:</span>{' '}
                    {selectedFile.generalExam.otherAbnormalities}
                  </div>
                  <div className="flex space-x-8 mt-2" data-id="element-3468" data-mp="2k9d372obpupa">
                    <div data-id="element-3469" data-mp="2f98nvfz5j60n">
                      <span className="font-semibold" data-id="element-3470" data-mp="2fa06lq7nh99i">MS Urine: SG:</span>{' '}
                      {selectedFile.generalExam.msUrine.sg}
                    </div>
                    <div data-id="element-3471" data-mp="2b9gcywmacns4">
                      <span className="font-semibold" data-id="element-3472" data-mp="2680qd04gm97m">Albumen:</span>{' '}
                      {selectedFile.generalExam.msUrine.albumen}
                    </div>
                    <div data-id="element-3473" data-mp="3u3fax70pg548">
                      <span className="font-semibold" data-id="element-3474" data-mp="2yp2uw2d2f8n">Acetone:</span>{' '}
                      {selectedFile.generalExam.msUrine.acetone}
                    </div>
                    <div data-id="element-3475" data-mp="3l6mr84zlu60w">
                      <span className="font-semibold" data-id="element-3476" data-mp="3p5vkz9una3wg">Sugar:</span>{' '}
                      {selectedFile.generalExam.msUrine.sugar}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3477" data-mp="3bcxkj93chlm1">
                    <div data-id="element-3478" data-mp="3g6ijb3crijma">
                      <span className="font-semibold" data-id="element-3479" data-mp="3g6s32977rujj">Hb%:</span>{' '}
                      {selectedFile.generalExam.hb}{' '}
                      <span className="italic text-xs" data-id="element-3480" data-mp="36fnohalgabp8">
                        (NOTE: Refer to UTH if HB% is 6grams or below)
                      </span>
                    </div>
                    <div data-id="element-3481" data-mp="2shzcks9d2ewb">
                      <span className="font-semibold" data-id="element-3482" data-mp="2xeps9eh978h7">ABO:</span>{' '}
                      {selectedFile.generalExam.abo}
                    </div>
                    <div data-id="element-3483" data-mp="2dtpkh6ccusup">
                      <span className="font-semibold" data-id="element-3484" data-mp="2dv0on79pq2mc">Rh:</span>{' '}
                      {selectedFile.generalExam.rh}
                    </div>
                  </div>
                  {(selectedFile.generalExam.notes || []).length > 0 && <div className="mt-2" data-id="element-3485" data-mp="2is0srcn04vat">
                      <span className="font-semibold" data-id="element-3486" data-mp="2iudappsrf5nv">Examination Notes:</span>
                      <ul className="mt-1 space-y-1" data-id="element-3487" data-mp="2wqh095soupu0">
                        {(selectedFile.generalExam.notes || []).map(note => <li key={note.id} data-id="element-3488" data-mp="36hv75ymyyrx6">
                            <span className="whitespace-pre-wrap" data-id="element-3489" data-mp="36gtmd2g5ir29">
                              {note.text}
                            </span>{' '}
                            <span className="text-xs italic" data-id="element-3490" data-mp="31l4jap4xas17">
                              — {note.recordedBy},{' '}
                              {new Date(note.recordedAt).toLocaleString()}
                            </span>
                          </li>)}
                      </ul>
                    </div>}
                </div>
              </div>

              {/* PRESENT PREGNANCY */}
              <div className="mb-6" data-id="element-3491" data-mp="v8aapt5p56i">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3492" data-mp="u6widkra4l3">
                  PRESENT PREGNANCY
                </h3>
                <div className="space-y-2" data-id="element-3493" data-mp="3rzgbzbb4j0gg">
                  <div className="flex space-x-8" data-id="element-3494" data-mp="34cqdul1wbq14">
                    <div data-id="element-3495" data-mp="349cfdnwcs0yy">
                      <span className="font-semibold" data-id="element-3496" data-mp="2zd408ai5o7rs">LMP:</span>{' '}
                      {selectedFile.presentPregnancy.lmp}
                    </div>
                    <div data-id="element-3497" data-mp="3e3l2tx6j13q9">
                      <span className="font-semibold" data-id="element-3498" data-mp="39aruz0frhf9y">Quickening:</span>{' '}
                      {selectedFile.presentPregnancy.quickening}
                    </div>
                    <div data-id="element-3499" data-mp="2ljcttazq140a">
                      <span className="font-semibold" data-id="element-3500" data-mp="2gm45strw65r4">EDD:</span>{' '}
                      {selectedFile.presentPregnancy.edd}
                    </div>
                    <div data-id="element-3501" data-mp="w3a8zwns4dh7">
                      <span className="font-semibold" data-id="element-3502" data-mp="w1z39rarwvng">Gestation Age:</span>{' '}
                      {selectedFile.presentPregnancy.gestationAge}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3503" data-mp="n1aogudk2r9i">
                    <div data-id="element-3504" data-mp="r47dp3xlplmf">
                      <span className="font-semibold" data-id="element-3505" data-mp="r5idytru8b8s">
                        Summary of ANC Findings 1st BP:
                      </span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryFirstBp}
                    </div>
                    <div data-id="element-3506" data-mp="15tvd1zm82a7i">
                      <span className="font-semibold" data-id="element-3507" data-mp="15uoag9zajncz">at:</span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryAtWeeks} Weeks
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3508" data-mp="3qjuj23jpi97h">
                    <div data-id="element-3509" data-mp="3qm6xw7u643gb">
                      <span className="font-semibold" data-id="element-3510" data-mp="3vixdku228x17">
                        Most recent Syphilis Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.date}
                    </div>
                    <div data-id="element-3511" data-mp="1w56uiftb0gv7">
                      <span className="font-semibold" data-id="element-3512" data-mp="1w5ys1w6gooo4">Result:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.result}
                    </div>
                    <div data-id="element-3513" data-mp="1naskarnmrwiy">
                      <span className="font-semibold" data-id="element-3514" data-mp="1n9hgwsy3kqsd">Treatment:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.treatment}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3515" data-mp="1dfh4s74eela5">
                    <div data-id="element-3516" data-mp="18djewu1hss88">
                      <span className="font-semibold" data-id="element-3517" data-mp="18etbfesp5scr">
                        Most recent HP Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.hpTest.date}
                    </div>
                    <div data-id="element-3518" data-mp="32pzqmad24ykh">
                      <span className="font-semibold" data-id="element-3519" data-mp="2yq7gvubxgmdj">Result:</span>{' '}
                      {selectedFile.presentPregnancy.hpTest.result}
                    </div>
                  </div>
                  <div data-id="element-3520" data-mp="phsy3oanwcpp">
                    <span className="font-semibold" data-id="element-3521" data-mp="ph0z03huvwuo">Pelvic Assessment:</span>{' '}
                    {selectedFile.presentPregnancy.pelvicAssessment}
                  </div>
                  <div data-id="element-3522" data-mp="ya4kmz2nng0e">
                    <span className="font-semibold" data-id="element-3523" data-mp="y9b9zn0ks6cz">Abnormalities:</span>{' '}
                    {selectedFile.presentPregnancy.abnormalities}
                  </div>
                </div>
              </div>

              {/* OBSERVATIONS FOR LATENT PHASE LABOUR */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3524" data-mp="al1u11q1mctr">
                <h3 className="font-bold uppercase underline mb-1" data-id="element-3525" data-mp="amwdeblo1wvq">
                  OBSERVATIONS FOR LATENT PHASE LABOUR
                </h3>
                <p className="text-xs italic mb-2" data-id="element-3526" data-mp="1m0qhrvb4ffvm">
                  NOTE: Refer to UTH if latest phase exceeds 8 hours
                </p>
                <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-3527" data-mp="1qx76orq59y9r">
                  <thead data-id="element-3528" data-mp="1pi4qmm9it57y">
                    <tr className="bg-gray-100" data-id="element-3529" data-mp="1kirutc0o8nt8">
                      <th className="border border-black p-1" data-id="element-3530" data-mp="1kk376086n3vy">Date</th>
                      <th className="border border-black p-1" data-id="element-3531" data-mp="1yj2n8jhmqagi">Time</th>
                      <th className="border border-black p-1" data-id="element-3532" data-mp="1yg84cwe9kxp7">Pulse</th>
                      <th className="border border-black p-1" data-id="element-3533" data-mp="1ufo3piuhfbyb">BP</th>
                      <th className="border border-black p-1" data-id="element-3534" data-mp="28fp5r1mhgpfd">FH</th>
                      <th className="border border-black p-1" data-id="element-3535" data-mp="23hxdg6hxf1an">Temp</th>
                      <th className="border border-black p-1" data-id="element-3536" data-mp="23fjjk0yzfv4o">Urinalysis</th>
                      <th className="border border-black p-1" data-id="element-3537" data-mp="2i60m5nncsr35">Contractions</th>
                      <th className="border border-black p-1" data-id="element-3538" data-mp="2d8i8xl2azppc">
                        Cervical Dilation
                      </th>
                      <th className="border border-black p-1" data-id="element-3539" data-mp="2r7hx6rlwm2kz">
                        Membranes Liquor
                      </th>
                      <th className="border border-black p-1" data-id="element-3540" data-mp="35txevt59lewr">Remarks</th>
                      <th className="border border-black p-1" data-id="element-3541" data-mp="35rufwjm4wbfw">Sign</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3542" data-mp="2wt93753cfqn1">
                    {selectedFile.labourObservations.length > 0 ? selectedFile.labourObservations.map(obs => <tr key={obs.id} data-id="element-3543" data-mp="30t12er6ukkfq">
                          <td className="border border-black p-1" data-id="element-3544" data-mp="2n3phlro8xioh">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3545" data-mp="2ruxqmmlnz214">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-3546" data-mp="2d3f2u2mkc377">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-3547" data-mp="244bjcxjhhls6">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-3548" data-mp="293eqqomxs43b">{obs.fh}</td>
                          <td className="border border-black p-1" data-id="element-3549" data-mp="295hef20m9l4v">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3550" data-mp="9w7toicpe6t3">
                            {obs.urinalysis}
                          </td>
                          <td className="border border-black p-1" data-id="element-3551" data-mp="4xwhi0yxrk7p">
                            {obs.contractions}
                          </td>
                          <td className="border border-black p-1" data-id="element-3552" data-mp="xm25cwladpbr">
                            {obs.cervicalDilation}
                          </td>
                          <td className="border border-black p-1" data-id="element-3553" data-mp="snqviluzretj">
                            {obs.membranesLiquor}
                          </td>
                          <td className="border border-black p-1 text-left" data-id="element-3554" data-mp="jmsv0djrhpfp">
                            {obs.remarks}
                          </td>
                          <td className="border border-black p-1" data-id="element-3555" data-mp="39dfowua6hp5o">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-3556" data-mp="1jsmnkjylatx8">
                        <td colSpan={12} className="border border-black p-4 text-gray-400 italic" data-id="element-3557" data-mp="1otkepm1kuyav">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION OF LABOUR AND DELIVERY */}
              <div className="mb-6" data-id="element-3558" data-mp="27j8v8a4omc6p">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3559" data-mp="27mejxo9efc1w">
                  DESCRIPTION OF LABOUR AND DELIVERY
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3560" data-mp="2gli2mr4nlpfv">
                  <div data-id="element-3561" data-mp="2ginkx7d0yiq5">
                    <span className="font-semibold" data-id="element-3562" data-mp="i5nawk55h2q6">Laceration:</span>{' '}
                    {selectedFile.delivery.laceration} |{' '}
                    <span className="font-semibold" data-id="element-3563" data-mp="m7qmr9vqe9bz">Anterior:</span>{' '}
                    {selectedFile.delivery.anterior} |{' '}
                    <span className="font-semibold" data-id="element-3564" data-mp="38dp4zkgu78p">Posterior:</span>{' '}
                    {selectedFile.delivery.posterior} |{' '}
                    <span className="font-semibold" data-id="element-3565" data-mp="c3jxa577l1ee">None:</span>{' '}
                    {selectedFile.delivery.none}
                  </div>
                  <div data-id="element-3566" data-mp="81ynispz138f">
                    <span className="font-semibold" data-id="element-3567" data-mp="83t1fn2trv30">Repaired:</span>{' '}
                    {selectedFile.delivery.repaired}
                  </div>
                  <div data-id="element-3568" data-mp="3bj74lacvjz23">
                    <span className="font-semibold" data-id="element-3569" data-mp="3bk03jp5ldk9o">Membranes:</span>{' '}
                    {selectedFile.delivery.membranes}
                  </div>
                  <div data-id="element-3570" data-mp="3kic4ol4340gd">
                    <span className="font-semibold" data-id="element-3571" data-mp="3khsnx7q67i3x">Date:</span>{' '}
                    {selectedFile.delivery.membranesDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3572" data-mp="14tskwckike0d">Time:</span>{' '}
                    {selectedFile.delivery.membranesTime}
                  </div>
                  <div data-id="element-3573" data-mp="189difaxcuuyd">
                    <span className="font-semibold" data-id="element-3574" data-mp="188278q1kykx8">Episiotomy:</span>{' '}
                    {selectedFile.delivery.episiotomy}
                  </div>
                  <div data-id="element-3575" data-mp="ybpgo11c0n6p">
                    <span className="font-semibold" data-id="element-3576" data-mp="ych8r1vq02s8">Repaired:</span>{' '}
                    {selectedFile.delivery.episiotomyRepaired}
                  </div>
                  <div data-id="element-3577" data-mp="1r04p24pyk792">
                    <span className="font-semibold" data-id="element-3578" data-mp="1qzuz2h4wy03d">Maternal ARV:</span>{' '}
                    {selectedFile.delivery.maternalARV}
                  </div>
                  <div data-id="element-3579" data-mp="1w1ai63nj5wj4">
                    <span className="font-semibold" data-id="element-3580" data-mp="1hal5sxyc2bdo">
                      Which ones (NPV, AZT, OTHER):
                    </span>{' '}
                    {selectedFile.delivery.arvTypes}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-3581" data-mp="29yh0jx4cdt7t">
                    <div data-id="element-3582" data-mp="29x5x5yet6nh8">
                      <span className="font-semibold" data-id="element-3583" data-mp="2epfmpdc0hliz">Uterine massage:</span>{' '}
                      {selectedFile.delivery.uterineMassage}
                    </div>
                    <div data-id="element-3584" data-mp="3gnlu6i4gkrpo">
                      <span className="font-semibold" data-id="element-3585" data-mp="3lojep1cxxxuc">
                        Mother Already on HAART:
                      </span>{' '}
                      {selectedFile.delivery.motherOnHAART}
                    </div>
                    <div data-id="element-3586" data-mp="3vdksckw0k355">
                      <span className="font-semibold" data-id="element-3587" data-mp="3ve2ufy5cxbkz">Cytotec:</span>{' '}
                      {selectedFile.delivery.cytotec}
                    </div>
                    <div data-id="element-3588" data-mp="32pf61tpba637">
                      <span className="font-semibold" data-id="element-3589" data-mp="32qqa7umo5fuu">Syntometrine:</span>{' '}
                      {selectedFile.delivery.syntometrine}
                    </div>
                  </div>
                  <div data-id="element-3590" data-mp="3bqvgg1g4m87f">
                    <span className="font-semibold" data-id="element-3591" data-mp="3bs6m66t4tq16">1st stage began Date:</span>{' '}
                    {selectedFile.delivery.firstStageBeganDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3592" data-mp="r04jizzzozni">Time:</span>{' '}
                    {selectedFile.delivery.firstStageBeganTime}
                  </div>
                  <div data-id="element-3593" data-mp="2bkjcts9z8kxx">
                    <span className="font-semibold" data-id="element-3594" data-mp="27gto92tay3cw">Placenta Delivery:</span>{' '}
                    {selectedFile.delivery.placentaDelivery}
                  </div>
                  <div data-id="element-3595" data-mp="22oaekgxqjsdd">
                    <span className="font-semibold" data-id="element-3596" data-mp="22nsbp1gkifwh">
                      Complete dilation Date:
                    </span>{' '}
                    {selectedFile.delivery.completeDilationDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3597" data-mp="1nsuf1wedz49l">Time:</span>{' '}
                    {selectedFile.delivery.completeDilationTime}
                  </div>
                  <div data-id="element-3598" data-mp="1jrso02umhyip">
                    <span className="font-semibold" data-id="element-3599" data-mp="1jt3sy5zt1cbe">
                      Placenta Delivered Date:
                    </span>{' '}
                    {selectedFile.delivery.placentaDeliveredDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3600" data-mp="3e2xncuyofos8">Time:</span>{' '}
                    {selectedFile.delivery.placentaDeliveredTime}
                  </div>
                  <div className="col-span-2" data-id="element-3601" data-mp="3sa0qrd1so4nf">
                    <span className="font-semibold" data-id="element-3602" data-mp="w4ocsa7pccs">
                      Abnormality at Dilation:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDilation} |{' '}
                    <span className="font-semibold" data-id="element-3603" data-mp="3o8yy5f2duquf">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDilationDesc}
                  </div>
                  <div data-id="element-3604" data-mp="frav5hh4tf76">
                    <span className="font-semibold" data-id="element-3605" data-mp="jv0f1tmv38lv">
                      Postpartum hemorrhage:
                    </span>{' '}
                    {selectedFile.delivery.postpartumHemorrhage}
                  </div>
                  <div data-id="element-3606" data-mp="atsu0d9cw49q">
                    <span className="font-semibold" data-id="element-3607" data-mp="at0zl5rhwcl1">Delivery Method:</span>{' '}
                    {selectedFile.delivery.deliveryMethod}
                  </div>
                  <div data-id="element-3608" data-mp="2vk9vhbloidgk">
                    <span className="font-semibold" data-id="element-3609" data-mp="2vlkxb5vkdb51">EBL:</span>{' '}
                    {selectedFile.delivery.ebl} cc
                  </div>
                  <div className="col-span-2" data-id="element-3610" data-mp="2o34ykop50m3e">
                    <span className="font-semibold" data-id="element-3611" data-mp="2j2qoz27w1439">
                      Abnormality at Delivery:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDelivery} |{' '}
                    <span className="font-semibold" data-id="element-3612" data-mp="2s2do1hd59xt5">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDeliveryDesc}
                  </div>
                  <div className="col-span-2" data-id="element-3613" data-mp="20bg1yw5a9bgn">
                    <span className="font-semibold" data-id="element-3614" data-mp="20cr1ukvlxz2h">Complications:</span>{' '}
                    {selectedFile.delivery.complications}
                  </div>
                </div>

                <div className="mt-4" data-id="element-3615" data-mp="29bvytmritkcj">
                  <h4 className="font-bold text-sm mb-1" data-id="element-3616" data-mp="29atbtpxbk21w">DELIVERY SUMMARY:</h4>
                  <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-3617" data-mp="1ll8pcx1yimnv">
                    <thead data-id="element-3618" data-mp="1gmxhgrv82m8a">
                      <tr className="bg-gray-100" data-id="element-3619" data-mp="1glme2t5ovghp">
                        <th className="border border-black p-1" data-id="element-3620" data-mp="1ub8p16vmvydh">DATE</th>
                        <th className="border border-black p-1" data-id="element-3621" data-mp="1z90p4o6fmqsr">
                          TIME OF BIRTH
                        </th>
                        <th className="border border-black p-1" data-id="element-3622" data-mp="247lgrz7mc25l">SEX</th>
                        <th className="border border-black p-1" data-id="element-3623" data-mp="28zlnqbm3vvbo">WEIGHT</th>
                        <th className="border border-black p-1" data-id="element-3624" data-mp="2d0p2eusf3lbb">
                          DELIVERED BY
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3625" data-mp="2i1ut6n2ioyi7">
                      {selectedFile.delivery.deliverySummary.length > 0 ? selectedFile.delivery.deliverySummary.map(sum => <tr key={sum.id} data-id="element-3626" data-mp="2rx6bffd4wnup">
                            <td className="border border-black p-1" data-id="element-3627" data-mp="sldc3c2vj6et">
                              {sum.date}
                            </td>
                            <td className="border border-black p-1" data-id="element-3628" data-mp="xkggcub158lq">
                              {sum.timeOfBirth}
                            </td>
                            <td className="border border-black p-1" data-id="element-3629" data-mp="363q3x0p0rfld">
                              {sum.sex}
                            </td>
                            <td className="border border-black p-1" data-id="element-3630" data-mp="322o0e7o2khdl">
                              {sum.weight}
                            </td>
                            <td className="border border-black p-1" data-id="element-3631" data-mp="3tt297ph873ie">
                              {sum.deliveredBy}
                            </td>
                          </tr>) : <tr data-id="element-3632" data-mp="3krkvgdrajdwv">
                          <td colSpan={5} className="border border-black p-4 text-gray-400 italic" data-id="element-3633" data-mp="3kqj9vfcnf90w">
                            No delivery summary recorded
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NEW BORN EVALUATION */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3634" data-mp="2auw7odh8nzhr">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3635" data-mp="1wulhzdro0qy6">
                  NEW BORN EVALUATION
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3636" data-mp="21swr1ma4yxfc">
                  <div data-id="element-3637" data-mp="1n4s76ezckfuz">
                    <span className="font-semibold" data-id="element-3638" data-mp="1n4a5v3xtvbg7">EGA:</span>{' '}
                    {selectedFile.newbornEvaluation.ega}
                  </div>
                  <div data-id="element-3639" data-mp="1rwtinyooy2jy">
                    <span className="font-semibold" data-id="element-3640" data-mp="1e43ubnlowljo">ALIVE:</span>{' '}
                    {selectedFile.newbornEvaluation.alive}
                  </div>
                  <div data-id="element-3641" data-mp="1i6ics6lg5jaf">
                    <span className="font-semibold" data-id="element-3642" data-mp="1i3mdoexv4oke">Neonatal Death:</span>{' '}
                    {selectedFile.newbornEvaluation.neonatalDeath}
                  </div>
                  <div data-id="element-3643" data-mp="193pjxywm8zih">
                    <span className="font-semibold" data-id="element-3644" data-mp="19495dplh6619">
                      If still born or death? Cause:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.causeOfDeath}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-3645" data-mp="zhevlnz9icec">
                    <div data-id="element-3646" data-mp="zipyzmospi4x">
                      <span className="font-semibold" data-id="element-3647" data-mp="zf270rz013sb">Apgar 1 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar1}
                    </div>
                    <div data-id="element-3648" data-mp="18gibkj34ubom">
                      <span className="font-semibold" data-id="element-3649" data-mp="18fqjhi8quw33">Apgar 5 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar5}
                    </div>
                    <div data-id="element-3650" data-mp="1i7d4256z27ii">
                      <span className="font-semibold" data-id="element-3651" data-mp="1i9qu1zmsotj7">Apgar 10 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar10}
                    </div>
                  </div>
                  <div data-id="element-3652" data-mp="1r688bbqgh7j6">
                    <span className="font-semibold" data-id="element-3653" data-mp="1n797o3brbwxj">General Exam:</span>{' '}
                    {selectedFile.newbornEvaluation.generalExam}
                  </div>
                  <div data-id="element-3654" data-mp="2to1jfguy9kk2">
                    <span className="font-semibold" data-id="element-3655" data-mp="2yl1s7zj6p1ev">
                      Congenital Malformation:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformation}
                  </div>
                  <div className="col-span-2" data-id="element-3656" data-mp="37kos2gw9lz5t">
                    <span className="font-semibold" data-id="element-3657" data-mp="37jlyu2bgzkmq">If Yes Describe:</span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformationDesc}
                  </div>
                  <div data-id="element-3658" data-mp="2fusdxra4hj6t">
                    <span className="font-semibold" data-id="element-3659" data-mp="2fs69i0ij3jst">Infant ARVS?:</span>{' '}
                    {selectedFile.newbornEvaluation.infantARVs}
                  </div>
                  <div data-id="element-3660" data-mp="2oof3v0rnfs83">
                    <span className="font-semibold" data-id="element-3661" data-mp="2ooowyx7zqfi0">Inf Feeding:</span>{' '}
                    {selectedFile.newbornEvaluation.infantFeeding}
                  </div>
                  <div className="col-span-2" data-id="element-3662" data-mp="1w58gfczhjxe3">
                    <span className="font-semibold" data-id="element-3663" data-mp="1on1vzm4qrr2b">
                      RESUSCITATION METHODS:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.resuscitationMethods}
                  </div>
                </div>

                <div className="mt-4" data-id="element-3664" data-mp="1fl0zwsl8vb28">
                  <h4 className="font-bold text-sm mb-1 underline" data-id="element-3665" data-mp="1fo6tak0woz3r">
                    NEWBORN EXAMINATION
                  </h4>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3666" data-mp="15opkhtqcy53k">
                    <div data-id="element-3667" data-mp="10xg0b8nkvtz2">
                      <span className="font-semibold" data-id="element-3668" data-mp="10wxy7ve8ilj8">CROWN HEEL LENGTH:</span>{' '}
                      {selectedFile.newbornEvaluation.crownHeelLength}
                    </div>
                    <div data-id="element-3669" data-mp="rwris3yz7bhf">
                      <span className="font-semibold" data-id="element-3670" data-mp="rxklmtutdgua">HEAD CIRCUMFERENCE:</span>{' '}
                      {selectedFile.newbornEvaluation.headCircumference}
                    </div>
                    <div data-id="element-3671" data-mp="hy222eitfx2r">
                      <span className="font-semibold" data-id="element-3672" data-mp="hzn04e4c9y5d">Skin: Jaundice:</span>{' '}
                      {selectedFile.newbornEvaluation.skinJaundice}
                    </div>
                    <div data-id="element-3673" data-mp="3hfw4a897w41s">
                      <span className="font-semibold" data-id="element-3674" data-mp="3hfnsyksqt6sp">Other:</span>{' '}
                      {selectedFile.newbornEvaluation.skinOther}
                    </div>
                    <div data-id="element-3675" data-mp="37ii31fvv7lu2">
                      <span className="font-semibold" data-id="element-3676" data-mp="37jb17sgrd30l">Fontanelles:</span>{' '}
                      {selectedFile.newbornEvaluation.fontanelles}
                    </div>
                    <div data-id="element-3677" data-mp="41sujocq221r">
                      <span className="font-semibold" data-id="element-3678" data-mp="42koyvul1tqg">Eyes:</span>{' '}
                      {selectedFile.newbornEvaluation.eyes} |{' '}
                      <span className="font-semibold" data-id="element-3679" data-mp="3v9x83gf10h90">Treatment:</span>{' '}
                      {selectedFile.newbornEvaluation.eyesTreatment}
                    </div>
                    <div data-id="element-3680" data-mp="2jtgp5d49jihe">
                      <span className="font-semibold" data-id="element-3681" data-mp="2jqkq1lgoinrd">Mouth:</span>{' '}
                      {selectedFile.newbornEvaluation.mouth}
                    </div>
                    <div data-id="element-3682" data-mp="ucbfrxih11lk">
                      <span className="font-semibold" data-id="element-3683" data-mp="ud4ov54wk36v">Neck:</span>{' '}
                      {selectedFile.newbornEvaluation.neck}
                    </div>
                    <div data-id="element-3684" data-mp="1497mpysq1fo0">
                      <span className="font-semibold" data-id="element-3685" data-mp="148xxidfi3cjd">Chest:</span>{' '}
                      {selectedFile.newbornEvaluation.chest}
                    </div>
                    <div data-id="element-3686" data-mp="1dzsvug60u2l6">
                      <span className="font-semibold" data-id="element-3687" data-mp="1e0arpboruesk">Abdomen:</span>{' '}
                      {selectedFile.newbornEvaluation.abdomen}
                    </div>
                    <div data-id="element-3688" data-mp="1n07ipjymvpqs">
                      <span className="font-semibold" data-id="element-3689" data-mp="1mzxrxu5rlek1">Umbilicus:</span>{' '}
                      {selectedFile.newbornEvaluation.umbilicus}
                    </div>
                    <div data-id="element-3690" data-mp="1rwxy052m6hb5">
                      <span className="font-semibold" data-id="element-3691" data-mp="3os3sao18qpix">Genitalia:</span>{' '}
                      {selectedFile.newbornEvaluation.genitalia} |{' '}
                      <span className="font-semibold" data-id="element-3692" data-mp="3tqx6yjs9kmko">Urine:</span>{' '}
                      {selectedFile.newbornEvaluation.urine}
                    </div>
                    <div data-id="element-3693" data-mp="3hp5opwprlooe">
                      <span className="font-semibold" data-id="element-3694" data-mp="3hpymw9anr5ux">Anus:</span>{' '}
                      {selectedFile.newbornEvaluation.anus}
                    </div>
                    <div data-id="element-3695" data-mp="3qpc11f9q8ilz">
                      <span className="font-semibold" data-id="element-3696" data-mp="3qo0s711fcko0">Meconium:</span>{' '}
                      {selectedFile.newbornEvaluation.meconium}
                    </div>
                    <div data-id="element-3697" data-mp="4i3x8heggjs2">
                      <span className="font-semibold" data-id="element-3698" data-mp="4ind7skjoy3g">Upper Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.upperLimbs}
                    </div>
                    <div data-id="element-3699" data-mp="da5yuj09g63x">
                      <span className="font-semibold" data-id="element-3700" data-mp="d9w46i49tarw">ERB Paralysis:</span>{' '}
                      {selectedFile.newbornEvaluation.erbParalysis}
                    </div>
                    <div data-id="element-3701" data-mp="n8ln8e7wl19q">
                      <span className="font-semibold" data-id="element-3702" data-mp="n94wz7negjco">Lower Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.lowerLimbs}
                    </div>
                    <div data-id="element-3703" data-mp="1avqp1m75wfje">
                      <span className="font-semibold" data-id="element-3704" data-mp="1aw0f19s7imp3">Ortolani Sign:</span>{' '}
                      {selectedFile.newbornEvaluation.ortolaniSign}
                    </div>
                    <div className="col-span-2" data-id="element-3705" data-mp="11usgcqls3juh">
                      <span className="font-semibold" data-id="element-3706" data-mp="11thbsokie82b">Back:</span>{' '}
                      {selectedFile.newbornEvaluation.back}
                    </div>
                    <div className="col-span-2 flex space-x-8" data-id="element-3707" data-mp="rwmiql708zvf">
                      <span className="font-semibold" data-id="element-3708" data-mp="ryz83jf3ecii">Reflexes:</span>
                      <div data-id="element-3709" data-mp="e6rajpfzihir">
                        <span className="font-semibold" data-id="element-3710" data-mp="e3dbor6j4qg2">Grasp:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesGrasp}
                      </div>
                      <div data-id="element-3711" data-mp="48levpkru7mc">
                        <span className="font-semibold" data-id="element-3712" data-mp="49d66o7c5j6t">Moro (Startle):</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesMoro}
                      </div>
                      <div data-id="element-3713" data-mp="1cghh5yeyc0c2">
                        <span className="font-semibold" data-id="element-3714" data-mp="17i7l7llekvpe">Sucking:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesSucking}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4" data-id="element-3715" data-mp="xrd0wmrpw66l">
                  <span className="font-semibold" data-id="element-3716" data-mp="xqa5c1jg9fkc">NEWBORN PROGRESS NOTES:</span>
                  <p className="mt-1 whitespace-pre-wrap" data-id="element-3717" data-mp="suutff505tqk">
                    {selectedFile.newbornEvaluation.progressNotes}
                  </p>
                </div>
              </div>

              {/* POST DELIVERY OBSERVATION CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3718" data-mp="ets6z1a3ihf4">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3719" data-mp="evv7wgcsduym">
                  POST DELIVERY OBSERVATION CHART
                </h3>
                <table className="w-full text-[10px] border-collapse border border-black text-center" data-id="element-3720" data-mp="2979yt0hja0fy">
                  <thead data-id="element-3721" data-mp="2grubksfkp4vl">
                    <tr className="bg-gray-100" data-id="element-3722" data-mp="2gqj7eri7tv3y">
                      <th className="border border-black p-1" colSpan={2} data-id="element-3723" data-mp="2lpvwliwk7e9p">
                        TIME
                      </th>
                      <th className="border border-black p-1" colSpan={8} data-id="element-3724" data-mp="2qhx3sqeveesv">
                        MOTHER
                      </th>
                      <th className="border border-black p-1" colSpan={7} data-id="element-3725" data-mp="2vi1kohjoxbjm">
                        BABY
                      </th>
                      <th className="border border-black p-1" rowSpan={2} data-id="element-3726" data-mp="22r1eq11stxpb">
                        SIGN
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3727" data-mp="27ot9d2ttvxx7">
                      <th className="border border-black p-1" data-id="element-3728" data-mp="2cq0e0t0ocgzn">DATE</th>
                      <th className="border border-black p-1" data-id="element-3729" data-mp="2cn4gh5sqnubq">TIME</th>
                      <th className="border border-black p-1" data-id="element-3730" data-mp="3j51ar0xix37a">BP</th>
                      <th className="border border-black p-1" data-id="element-3731" data-mp="3o3upsxsgl29k">PULSE</th>
                      <th className="border border-black p-1" data-id="element-3732" data-mp="1o2edllmi8ewm">RESPS</th>
                      <th className="border border-black p-1" data-id="element-3733" data-mp="1o4hex1t3xugn">TEMP</th>
                      <th className="border border-black p-1" data-id="element-3734" data-mp="1j2i97l9walgj">PV BLEEDING</th>
                      <th className="border border-black p-1" data-id="element-3735" data-mp="1x3clif02d6kh">UTERUS</th>
                      <th className="border border-black p-1" data-id="element-3736" data-mp="1t1hk7ai4ut6p">BLADDER</th>
                      <th className="border border-black p-1" data-id="element-3737" data-mp="1szd7c329ajua">PALLOR</th>
                      <th className="border border-black p-1" data-id="element-3738" data-mp="15con3apryfai">TEMP</th>
                      <th className="border border-black p-1" data-id="element-3739" data-mp="10dbxii7iqu48">RESPS</th>
                      <th className="border border-black p-1" data-id="element-3740" data-mp="10azavrqtfvku">CORD</th>
                      <th className="border border-black p-1" data-id="element-3741" data-mp="1e5i3xckvdmqh">SKIN COLOUR</th>
                      <th className="border border-black p-1" data-id="element-3742" data-mp="19coysnlhocdv">
                        FEEDING (BF/AF)
                      </th>
                      <th className="border border-black p-1" data-id="element-3743" data-mp="lltaw1k5m1bw">BOWEL</th>
                      <th className="border border-black p-1" data-id="element-3744" data-mp="hlj80xrnrr19">URINE</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3745" data-mp="7h2sk5i3cbxz">
                    {selectedFile.postDeliveryObservations.length > 0 ? selectedFile.postDeliveryObservations.map(obs => <tr key={obs.id} data-id="element-3746" data-mp="3tqww4gu0hlzk">
                          <td className="border border-black p-1" data-id="element-3747" data-mp="2jao6c09excw">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3748" data-mp="q8lq3wtcixsm">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-3749" data-mp="v76t23zzfvkr">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-3750" data-mp="g9wa69l0fnf0">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-3751" data-mp="lblnxwzmaf6s">
                            {obs.resps}
                          </td>
                          <td className="border border-black p-1" data-id="element-3752" data-mp="3609ntug6ouch">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3753" data-mp="2nas1glzmjth7">
                            {obs.pvBleeding}
                          </td>
                          <td className="border border-black p-1" data-id="element-3754" data-mp="2db148cjoj0df">
                            {obs.uterus}
                          </td>
                          <td className="border border-black p-1" data-id="element-3755" data-mp="29b0q6t0hsr6w">
                            {obs.bladder}
                          </td>
                          <td className="border border-black p-1" data-id="element-3756" data-mp="361t1ldchdv2c">
                            {obs.pallor}
                          </td>
                          <td className="border border-black p-1" data-id="element-3757" data-mp="2w9djmg16p6hg">
                            {obs.babyTemp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3758" data-mp="2s6h4x0erluis">
                            {obs.babyResps}
                          </td>
                          <td className="border border-black p-1" data-id="element-3759" data-mp="3jz7tcoq7iexh">
                            {obs.cord}
                          </td>
                          <td className="border border-black p-1" data-id="element-3760" data-mp="16io86feocpyx">
                            {obs.skinColour}
                          </td>
                          <td className="border border-black p-1" data-id="element-3761" data-mp="1fhrtlq1bdhgl">
                            {obs.feedingBF} / {obs.feedingAF}
                          </td>
                          <td className="border border-black p-1" data-id="element-3762" data-mp="1kifsexvsb2ld">
                            {obs.eliminationBowel}
                          </td>
                          <td className="border border-black p-1" data-id="element-3763" data-mp="1uesm3vrbxgg4">
                            {obs.eliminationUrine}
                          </td>
                          <td className="border border-black p-1" data-id="element-3764" data-mp="1z6alwghm9azi">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-3765" data-mp="284cimjubhhgz">
                        <td colSpan={18} className="border border-black p-4 text-gray-400 italic" data-id="element-3766" data-mp="2d5abbqcydtwq">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DRUG CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3767" data-mp="39et5jyxbk2ft">
                <h3 className="font-bold uppercase underline mb-2 text-center" data-id="element-3768" data-mp="39gxkdbwraluv">
                  DRUG CHART
                </h3>
                <div className="flex justify-between mb-2 text-xs" data-id="element-3769" data-mp="51e741yu1moz">
                  <div data-id="element-3770" data-mp="52pai0od8sfk">
                    <span className="font-semibold" data-id="element-3771" data-mp="3nfdqb0mso8by">NAME:</span>{' '}
                    {selectedFile.patient.firstName}{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3772" data-mp="jxlsh5lz8u5u">
                    <span className="font-semibold" data-id="element-3773" data-mp="jx3vu7vekdxe">WARD:</span>{' '}
                    {selectedFile.drugChart?.ward}
                  </div>
                  <div data-id="element-3774" data-mp="oqoorli52hpq">
                    <span className="font-semibold" data-id="element-3775" data-mp="y3fa9xzfhuba">No.:</span>{' '}
                    {selectedFile.drugChart?.patientNo}
                  </div>
                  <div data-id="element-3776" data-mp="u1kbb04yzt0o">
                    <span className="font-semibold" data-id="element-3777" data-mp="u099h5v34vc7">SEX/AGE:</span> Female /{' '}
                    {selectedFile.patient.age}
                  </div>
                </div>

                <table className="w-full text-[9px] border-collapse border border-black text-center" data-id="element-3778" data-mp="12uxdytsu68zy">
                  <thead data-id="element-3779" data-mp="f5332hs2uuic">
                    <tr className="bg-gray-100" data-id="element-3780" data-mp="f6mh01q9gtgy">
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3781" data-mp="b49cfgn931lr">
                        DATE
                      </th>
                      <th className="border border-black p-1 w-24" rowSpan={3} data-id="element-3782" data-mp="p0wcvteew9wf">
                        DRUG
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3783" data-mp="20hmmlmi1njus">
                        DOSAGE
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3784" data-mp="1vijgdyqbv7l8">
                        ROUTE OF ADMIN
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3785" data-mp="27lcfx80fro7o">
                        ORDERED BY
                      </th>
                      <th className="border border-black p-1" colSpan={21} data-id="element-3786" data-mp="2clqnyq21ey5p">
                        SUPPLIED BY (DATE)
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3787" data-mp="2hh5vz1dd5zu7">
                        SUPPLIED BY
                      </th>
                      <th className="border border-black p-1 w-12" rowSpan={3} data-id="element-3788" data-mp="1ovwbwapjldmi">
                        QUANTITY
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3789" data-mp="1truv7jw0k5g2">
                        DATE
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3790" data-mp="1148t6g2fp8vb">
                      {[0, 1, 2].map(colIdx => <th key={colIdx} className="border border-black p-1" colSpan={7} data-id="element-3791" data-mp="163bo37op9ypk">
                          {selectedFile.drugChart?.supplyDates?.[colIdx] || ''}
                        </th>)}
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3792" data-mp="jnldhg1py1ro">
                      {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-3793" data-mp="flzudcylcr91">
                          {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="border border-black p-0.5 font-normal" data-id="element-3794" data-mp="at6nojjkb8ub">
                                {time}
                              </th>)}
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-3795" data-mp="gmjlwq6c7k8l">
                    {selectedFile.drugChart?.rows && selectedFile.drugChart.rows.length > 0 ? selectedFile.drugChart.rows.map(row => <tr key={row.id} data-id="element-3796" data-mp="7tonafbdkmrs">
                          <td className="border border-black p-1" data-id="element-3797" data-mp="7sn9w46vzo6w">
                            {row.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3798" data-mp="3u1etkrjwjw22">
                            {row.drug}
                          </td>
                          <td className="border border-black p-1" data-id="element-3799" data-mp="2x7zxnc02cfr">
                            {row.dosage}
                          </td>
                          <td className="border border-black p-1" data-id="element-3800" data-mp="3p2v5akhu686j">
                            {row.routeOfAdmin}
                          </td>
                          <td className="border border-black p-1" data-id="element-3801" data-mp="1jay6d0qldcsk">
                            {row.orderedBy}
                          </td>
                          {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-3802" data-mp="37ukgb26p5u41">
                              {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                      const key = `${colIdx}-${time}`;
                      return <td key={key} className="border border-black p-0.5" data-id="element-3803" data-mp="3crkkf7jzkmsi">
                                      {row.administration?.[key] || ''}
                                    </td>;
                    })}
                            </Fragment>)}
                          <td className="border border-black p-1" data-id="element-3804" data-mp="263njqeiuv7c1">
                            {row.suppliedBy}
                          </td>
                          <td className="border border-black p-1" data-id="element-3805" data-mp="216x2hnvbe5p1">
                            {row.quantity}
                          </td>
                          <td className="border border-black p-1" data-id="element-3806" data-mp="a0sgcgx8co52">
                            {row.suppliedDate}
                          </td>
                        </tr>) : <tr data-id="element-3807" data-mp="j1yzyyv6ef2j">
                        <td colSpan={30} className="border border-black p-4 text-gray-400 italic" data-id="element-3808" data-mp="j3a4j0wg3qup">
                          No drug chart recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
                <div className="flex justify-between mt-4 text-xs" data-id="element-3809" data-mp="12pg7uu8ko9r2">
                  <div data-id="element-3810" data-mp="12rsliv7arxyb">
                    <span className="font-semibold" data-id="element-3811" data-mp="16rkz5mbjsuaj">DATE:</span>{' '}
                    {selectedFile.drugChart?.signedDate}
                  </div>
                  <div data-id="element-3812" data-mp="1bqxku3qonvbj">
                    <span className="font-semibold" data-id="element-3813" data-mp="1bobbpzo597r7">SIGNATURE:</span>{' '}
                    {selectedFile.drugChart?.signedBy}
                  </div>
                </div>
              </div>

              {/* PUERPERIUM NOTES */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3814" data-mp="2n78lezlim986">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3815" data-mp="2n9larxtlrlv9">
                  PUERPERIUM NOTES
                </h3>
                <table className="w-full text-sm border-collapse border border-black" data-id="element-3816" data-mp="3ezg0vwpabfkz">
                  <thead data-id="element-3817" data-mp="3axl3326kbkby">
                    <tr className="bg-gray-100" data-id="element-3818" data-mp="3aye19ergh1ih">
                      <th className="border border-black p-2 text-left w-32" data-id="element-3819" data-mp="3azp51ekwi99l">
                        DATE
                      </th>
                      <th className="border border-black p-2 text-left" data-id="element-3820" data-mp="3181k7wiunqh3">
                        NOTES
                      </th>
                      <th className="border border-black p-2 text-left w-48" data-id="element-3821" data-mp="2ib1rwahyj58">
                        SIGNATURE
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3822" data-mp="327kuiuy71i3v">
                    {selectedFile.puerperiumNotes.length > 0 ? selectedFile.puerperiumNotes.map(note => <tr key={note.id} data-id="element-3823" data-mp="2sapyqjtb1vta">
                          <td className="border border-black p-2" data-id="element-3824" data-mp="2sbr8mkyl4c9f">
                            {note.date}
                          </td>
                          <td className="border border-black p-2 whitespace-pre-wrap" data-id="element-3825" data-mp="2x76lb9kuw249">
                            {note.notes}
                          </td>
                          <td className="border border-black p-2" data-id="element-3826" data-mp="3pwvr008igqc7">
                            {note.signature}
                          </td>
                        </tr>) : <tr data-id="element-3827" data-mp="3fy7lg0xpq3pe">
                        <td colSpan={3} className="border border-black p-4 text-gray-400 italic text-center" data-id="element-3828" data-mp="20ja4q4pf56t1">
                          No notes recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DISCHARGE SLIP */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3829" data-mp="26z8yffg750dg">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3830" data-mp="26x66uqzeaz6m">
                  DISCHARGE SLIP FOR LABOUR WARD
                </h3>

                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm" data-id="element-3831" data-mp="1ebwhbkst1krj">
                  <div data-id="element-3832" data-mp="19cjmacrs57dv">
                    <span className="font-semibold" data-id="element-3833" data-mp="19d331q5p1pqb">SM No:</span>{' '}
                    {selectedFile.dischargeSlip?.smNo}
                  </div>
                  <div data-id="element-3834" data-mp="1j9y4ohx9k68t">
                    <span className="font-semibold" data-id="element-3835" data-mp="1j9oasj93lexu">Gravida:</span>{' '}
                    {selectedFile.dischargeSlip?.gravida}
                  </div>
                  <div data-id="element-3836" data-mp="vi92m9un43eo">
                    <span className="font-semibold" data-id="element-3837" data-mp="qlip9ua7zlwy">Patient's Name:</span>{' '}
                    {selectedFile.dischargeSlip?.patientName || `${selectedFile.patient.firstName} ${selectedFile.patient.surname}`}
                  </div>
                  <div data-id="element-3838" data-mp="23uutindmq6a1">
                    <span className="font-semibold" data-id="element-3839" data-mp="23rgtvmwcob6a">Party:</span>{' '}
                    {selectedFile.dischargeSlip?.party}
                  </div>
                  <div data-id="element-3840" data-mp="2csx33rbfi78x">
                    <span className="font-semibold" data-id="element-3841" data-mp="2csoq7zfb31xq">Time of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.timeOfDischarge}
                  </div>
                  <div data-id="element-3842" data-mp="2miq45ydy33yx">
                    <span className="font-semibold" data-id="element-3843" data-mp="2mj9plp2t0ahp">Age:</span>{' '}
                    {selectedFile.dischargeSlip?.age || selectedFile.patient.age}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3844" data-mp="2wfupxx3voxf7">
                  Labour and Delivery Summary
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm border border-black p-3" data-id="element-3845" data-mp="124ps59fh3f7s">
                  <div className="font-bold uppercase" data-id="element-3846" data-mp="rzr90zgwyjmk">Mother</div>
                  <div className="font-bold uppercase" data-id="element-3847" data-mp="w1l50ug5yzge">Infant</div>
                  <div data-id="element-3848" data-mp="w0t9tkqhb3qn">
                    <span className="font-semibold" data-id="element-3849" data-mp="w3fkht8o1zd3">D.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.motherDob}
                  </div>
                  <div data-id="element-3850" data-mp="n7zrtfjndq93">
                    <span className="font-semibold" data-id="element-3851" data-mp="n7g6dousgjqb">T.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.infantTob}
                  </div>
                  <div data-id="element-3852" data-mp="dbd84u32558n">
                    <span className="font-semibold" data-id="element-3853" data-mp="dbwinpqdorcn">M.O.D:</span>{' '}
                    {selectedFile.dischargeSlip?.modeOfDelivery}
                  </div>
                  <div data-id="element-3854" data-mp="4a5dqn3nwoju">
                    <span className="font-semibold" data-id="element-3855" data-mp="4bgcg8i93643">A/S:</span>{' '}
                    {selectedFile.dischargeSlip?.apgarScore}
                  </div>
                  <div data-id="element-3856" data-mp="1tnil6l72msp6">
                    <span className="font-semibold" data-id="element-3857" data-mp="1yozjc8y68122">Perineum:</span>{' '}
                    {selectedFile.dischargeSlip?.perineum}
                  </div>
                  <div data-id="element-3858" data-mp="1wb413mu2bc2x">
                    <span className="font-semibold" data-id="element-3859" data-mp="1s87hptwp7bxx">Sex:</span>{' '}
                    {selectedFile.dischargeSlip?.infantSex}
                  </div>
                  <div className="col-span-2" data-id="element-3860" data-mp="1n9w73gykwxen">
                    <span className="font-semibold" data-id="element-3861" data-mp="1ia03hitsn7zm">Comments:</span>{' '}
                    {selectedFile.dischargeSlip?.comments}
                  </div>
                  <div className="col-span-2" data-id="element-3862" data-mp="2fx8u1s8nj78h">
                    <span className="font-semibold" data-id="element-3863" data-mp="2ayxj1e6meqoo">P.C.A:</span>{' '}
                    {selectedFile.dischargeSlip?.pca}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2" data-id="element-3864" data-mp="265w0mw77g90c">
                  <h4 className="font-bold uppercase underline text-sm" data-id="element-3865" data-mp="2182x6axadt3r">
                    Discharge Summary
                  </h4>
                  <div className="text-sm" data-id="element-3866" data-mp="2yq2yvtck9iyz">
                    <span className="font-semibold" data-id="element-3867" data-mp="2tx9r0wlspuio">Date of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.dateOfDischarge}
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3868" data-mp="366zgyitf5uyb">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3869" data-mp="3b6vmwnlofwgi">Mother</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3870" data-mp="3b5kj4ns8eope">
                    <div data-id="element-3871" data-mp="3g701g830yh43">
                      <span className="font-semibold" data-id="element-3872" data-mp="3g79wwb6u9gh6">BP:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bp}
                    </div>
                    <div data-id="element-3873" data-mp="3p5vart1d87bm">
                      <span className="font-semibold" data-id="element-3874" data-mp="3p52bte8nem41">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.pallor}
                    </div>
                    <div data-id="element-3875" data-mp="2rlmrl1cl1bd">
                      <span className="font-semibold" data-id="element-3876" data-mp="2pr2m8xwhd8c">Breast:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breast}
                    </div>
                    <div data-id="element-3877" data-mp="br8qidhjuj7l">
                      <span className="font-semibold" data-id="element-3878" data-mp="bss4fxfqgi67">
                        Breastfeeding (Yes/No):
                      </span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breastfeeding}
                    </div>
                    <div data-id="element-3879" data-mp="14fftijazgenv">
                      <span className="font-semibold" data-id="element-3880" data-mp="1i8p1qgn72u4t">Uterus:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.uterus}
                    </div>
                    <div data-id="element-3881" data-mp="1e850p1zi36de">
                      <span className="font-semibold" data-id="element-3882" data-mp="qhrbdj8avlmi">Perineum:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.perineum}
                    </div>
                    <div data-id="element-3883" data-mp="lkio548aoree">
                      <span className="font-semibold" data-id="element-3884" data-mp="zifpedxp1lze">Lochia:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.lochia}
                    </div>
                    <div data-id="element-3885" data-mp="ui1dulwvvtwm">
                      <span className="font-semibold" data-id="element-3886" data-mp="ulp5tgmok898">Bowels:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bowels}
                    </div>
                    <div data-id="element-3887" data-mp="25xzrz46x564p">
                      <span className="font-semibold" data-id="element-3888" data-mp="260vqatmohwto">Urine:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.urine}
                    </div>
                    <div data-id="element-3889" data-mp="2ihwrlgvtxhmx">
                      <span className="font-semibold" data-id="element-3890" data-mp="2if0utvvpwz02">Family Planning:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.familyPlanning}
                    </div>
                    <div className="col-span-2" data-id="element-3891" data-mp="29gyuzjnq0ced">
                      <span className="font-semibold" data-id="element-3892" data-mp="2x69n6cr08ygw">Danger Signs:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.dangerSigns}
                    </div>
                    <div className="col-span-2" data-id="element-3893" data-mp="326xez0n25jc6">
                      <span className="font-semibold" data-id="element-3894" data-mp="2nf6sptskzhql">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.treatment}
                    </div>
                    <div data-id="element-3895" data-mp="3g55gllv1h8to">
                      <span className="font-semibold" data-id="element-3896" data-mp="3g3uerrl5mb57">Vitamin A:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.vitaminA}
                    </div>
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3897" data-mp="3ur2laf3ipsau">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3898" data-mp="3ps857kwxqhuf">Infant</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3899" data-mp="3kteo7iifw8pi">
                    <div data-id="element-3900" data-mp="3krljw9vau0ko">
                      <span className="font-semibold" data-id="element-3901" data-mp="3grtgebkrikm6">BWT:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthWeight}
                    </div>
                    <div data-id="element-3902" data-mp="3bu1fis253o5u">
                      <span className="font-semibold" data-id="element-3903" data-mp="372057boj87ig">Discharge Weight:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.dischargeWeight}
                    </div>
                    <div data-id="element-3904" data-mp="320udnh6lyqai">
                      <span className="font-semibold" data-id="element-3905" data-mp="2x43xyuyptwpm">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.pallor}
                    </div>
                    <div data-id="element-3906" data-mp="2t4bkc3ugt0de">
                      <span className="font-semibold" data-id="element-3907" data-mp="2o35tycoa1p71">Jaundice:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.jaundice}
                    </div>
                    <div className="col-span-2" data-id="element-3908" data-mp="13ktqo4ffzx1c">
                      <span className="font-semibold" data-id="element-3909" data-mp="18jevkh5n34w4">
                        Passed Meconium / Urine:
                      </span>{' '}
                      {selectedFile.dischargeSlip?.infant?.passedMeconiumUrine}
                    </div>
                    <div data-id="element-3910" data-mp="1hk38t9n7l17y">
                      <span className="font-semibold" data-id="element-3911" data-mp="kulwibtq0gxa">Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feeding}
                    </div>
                    <div data-id="element-3912" data-mp="owgvh9o6ii7w">
                      <span className="font-semibold" data-id="element-3913" data-mp="oturtl4esmuy">Feeding Option:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feedingOption}
                    </div>
                    <div data-id="element-3914" data-mp="ynbm0q8gk3z5">
                      <span className="font-semibold" data-id="element-3915" data-mp="16mjeubooxop">Breast Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.breastFeeding}
                    </div>
                    <div data-id="element-3916" data-mp="63mtdgbnmkl3">
                      <span className="font-semibold" data-id="element-3917" data-mp="2vnj69f5w70k0">BCG:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.bcg}
                    </div>
                    <div data-id="element-3918" data-mp="2qqj1d7ks43uh">
                      <span className="font-semibold" data-id="element-3919" data-mp="34ohgif6xc4b1">OPV:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.opv}
                    </div>
                    <div data-id="element-3920" data-mp="30lb0wgxx90rw">
                      <span className="font-semibold" data-id="element-3921" data-mp="30p8mrabvw6fh">Birth Record:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthRecord}
                    </div>
                    <div data-id="element-3922" data-mp="39n0zq7u4v1zp">
                      <span className="font-semibold" data-id="element-3923" data-mp="39lpwc94lnw94">G/C:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.gc}
                    </div>
                    <div className="col-span-2" data-id="element-3924" data-mp="3jeomvwjnpwvd">
                      <span className="font-semibold" data-id="element-3925" data-mp="17qu4pr1ben6">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.treatment}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm" data-id="element-3926" data-mp="ftxyjyad1jn8">
                  <div data-id="element-3927" data-mp="fv93i1fjkxfx">
                    <span className="font-semibold" data-id="element-3928" data-mp="ktius0y5bdw9">
                      6 days Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixDaysPostnatalDate}
                  </div>
                  <div data-id="element-3929" data-mp="autlgwkbf1rm">
                    <span className="font-semibold" data-id="element-3930" data-mp="au1r1p2gfa2x">
                      6 Weeks Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixWeeksPostnatalDate}
                  </div>
                  <div data-id="element-3931" data-mp="1wrg64tjtmyj">
                    <span className="font-semibold" data-id="element-3932" data-mp="3jb69dy6o7j8g">Name in Full:</span>{' '}
                    {selectedFile.dischargeSlip?.nameInFull}
                  </div>
                </div>
              </div>

              {/* POSTNATAL AT ONE WEEK */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3933" data-mp="3absz53aq2qmo">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3934" data-mp="3f9uizqfz2tz7">
                  Postnatal at One Week
                </h3>
                <div className="text-sm mb-3" data-id="element-3935" data-mp="229ctxfrb3em8">
                  <span className="font-semibold" data-id="element-3936" data-mp="268lqesdqdqlh">Date:</span>{' '}
                  {selectedFile.postNatalOneWeek?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3937" data-mp="2b6wyoyodnt1l">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3938" data-mp="2g4yi5kpptudl">
                  <div data-id="element-3939" data-mp="2l65qbkvrsvks">
                    <span className="font-semibold" data-id="element-3940" data-mp="2p7zn3i2uhffo">General Well Being:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3941" data-mp="2u0sr23qhojqp">
                    <span className="font-semibold" data-id="element-3942" data-mp="2yxj7is67hhcn">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.temp}
                  </div>
                  <div data-id="element-3943" data-mp="33ygl0rg9tn7t">
                    <span className="font-semibold" data-id="element-3944" data-mp="33v2ldqyzrs42">Pulse:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.pulse}
                  </div>
                  <div data-id="element-3945" data-mp="x9em82t26vsj">
                    <span className="font-semibold" data-id="element-3946" data-mp="x6k2kdhvdf06">BP:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.bp}
                  </div>
                  <div data-id="element-3947" data-mp="n9nto6o26qv3">
                    <span className="font-semibold" data-id="element-3948" data-mp="naz76y7b3cze">Urine:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.urine}
                  </div>
                  <div data-id="element-3949" data-mp="1fznzc7qdw7z0">
                    <span className="font-semibold" data-id="element-3950" data-mp="1fy356j7zer1o">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.head}
                  </div>
                  <div data-id="element-3951" data-mp="169bfyiub2kug">
                    <span className="font-semibold" data-id="element-3952" data-mp="168jljbcg2t5r">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.eyes}
                  </div>
                  <div data-id="element-3953" data-mp="1185kwed6alhr">
                    <span className="font-semibold" data-id="element-3954" data-mp="1ypch0z585rwf">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ears}
                  </div>
                  <div data-id="element-3955" data-mp="1txkxh8khkmfs">
                    <span className="font-semibold" data-id="element-3956" data-mp="1tx2w5xiyvi10">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.mouth}
                  </div>
                  <div data-id="element-3957" data-mp="3t4jc77ftum7f">
                    <span className="font-semibold" data-id="element-3958" data-mp="3t7xaa3hgk992">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.neck}
                  </div>
                  <div data-id="element-3959" data-mp="3jezpfqozu87a">
                    <span className="font-semibold" data-id="element-3960" data-mp="3jegewv1oam3a">Breasts:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.breasts}
                  </div>
                  <div data-id="element-3961" data-mp="39i3pwah2owev">
                    <span className="font-semibold" data-id="element-3962" data-mp="39in0f64e8iiv">Ampills:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ampills}
                  </div>
                  <div data-id="element-3963" data-mp="30gndjx6p6k80">
                    <span className="font-semibold" data-id="element-3964" data-mp="30i8cdz01opbo">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3965" data-mp="yqbwkd6ugtb3">
                    <span className="font-semibold" data-id="element-3966" data-mp="yoher7qvdhb8">Groin:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.groin}
                  </div>
                  <div data-id="element-3967" data-mp="2e906e7fmtn99">
                    <span className="font-semibold" data-id="element-3968" data-mp="2e9t30fkvmwdo">Perineum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.perineum}
                  </div>
                  <div data-id="element-3969" data-mp="2o6xsopdxl9z8">
                    <span className="font-semibold" data-id="element-3970" data-mp="2o6nzksxlampb">Legs:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.legs}
                  </div>
                  <div data-id="element-3971" data-mp="3205jwfy3o3ge">
                    <span className="font-semibold" data-id="element-3972" data-mp="2wzhsht5ylkln">Anus:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.anus}
                  </div>
                  <div data-id="element-3973" data-mp="3bvqq6qa7bpvy">
                    <span className="font-semibold" data-id="element-3974" data-mp="36xfhwjzk1nfu">Lochia:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3975" data-mp="1hklhh7k9cn9r">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3976" data-mp="3mz9357ff6qe">
                  <div data-id="element-3977" data-mp="3m7lagk2mdao">
                    <span className="font-semibold" data-id="element-3978" data-mp="8lk8j2eutmds">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3979" data-mp="8ndj2ssl8qr2">
                      {selectedFile.postNatalOneWeek?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3980" data-mp="hk4nptl7hdx9">
                    <span className="font-semibold" data-id="element-3981" data-mp="3h1cftwndwg5m">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3982" data-mp="3gz9g2kwfj8np">
                      {selectedFile.postNatalOneWeek?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3983" data-mp="3q0r4cqjzqgnh">
                    <span className="font-semibold" data-id="element-3984" data-mp="3usqwvxxqe7a1">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3985" data-mp="3uuvc3c12yspm">
                      {selectedFile.postNatalOneWeek?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3986" data-mp="32a3hmz5f1mgv">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3987" data-mp="2ptlzfvxniw38">
                  <div data-id="element-3988" data-mp="2ls0hvxa69tmp">
                    <span className="font-semibold" data-id="element-3989" data-mp="2lr7hde1t40d0">Infant:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.name}
                  </div>
                  <div data-id="element-3990" data-mp="2bu2sh6gktqsi">
                    <span className="font-semibold" data-id="element-3991" data-mp="2bucj8w9g41z9">Weight:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.weight}
                  </div>
                  <div data-id="element-3992" data-mp="271jei8dz8tn6">
                    <span className="font-semibold" data-id="element-3993" data-mp="223zyb2xpy6sl">Date:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3994" data-mp="1y0v2fj8593cx">
                  <div data-id="element-3995" data-mp="1t5o1gj69ecrp">
                    <span className="font-semibold" data-id="element-3996" data-mp="1o4i4ua9ha5cw">Head Circumference:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.headCircumference}
                  </div>
                  <div data-id="element-3997" data-mp="1ej8v2dscmmv8">
                    <span className="font-semibold" data-id="element-3998" data-mp="1jfpljen0tdbh">Length:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.length}
                  </div>
                  <div data-id="element-3999" data-mp="14kjecoc6v8gk">
                    <span className="font-semibold" data-id="element-4000" data-mp="19htasnxzwkdw">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.temp}
                  </div>
                  <div data-id="element-4001" data-mp="1x7w04waizc8t">
                    <span className="font-semibold" data-id="element-4002" data-mp="1x9gr6bxmsd1x">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.head}
                  </div>
                  <div data-id="element-4003" data-mp="1ngj9g80gqs4d">
                    <span className="font-semibold" data-id="element-4004" data-mp="1nhareg150rc6">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.eyes}
                  </div>
                  <div data-id="element-4005" data-mp="2g5zv8mtkvarm">
                    <span className="font-semibold" data-id="element-4006" data-mp="2g5geh9fnysf6">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.ears}
                  </div>
                  <div data-id="element-4007" data-mp="9hptzejiwr59">
                    <span className="font-semibold" data-id="element-4008" data-mp="9gxuvtqpwba8">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.mouth}
                  </div>
                  <div data-id="element-4009" data-mp="j7sudzsz57dm">
                    <span className="font-semibold" data-id="element-4010" data-mp="j8amck8lszfq">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neck}
                  </div>
                  <div data-id="element-4011" data-mp="easn5lke1ykx">
                    <span className="font-semibold" data-id="element-4012" data-mp="3mo9dx12pzved">Chest:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.chest}
                  </div>
                  <div data-id="element-4013" data-mp="3hr96omu4wmlo">
                    <span className="font-semibold" data-id="element-4014" data-mp="3hrrcob6lmf6s">Abdomen:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.abdomen}
                  </div>
                  <div data-id="element-4015" data-mp="3rm14nrrmpbgz">
                    <span className="font-semibold" data-id="element-4016" data-mp="3rpp0ixkjq9yv">Cord:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.cord}
                  </div>
                  <div data-id="element-4017" data-mp="16wbsjmib83rz">
                    <span className="font-semibold" data-id="element-4018" data-mp="16x3myu067vgo">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-4019" data-mp="35fyqvob55rei">
                    <span className="font-semibold" data-id="element-4020" data-mp="35d2qzufqgsnf">Extremities:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.extremities}
                  </div>
                  <div data-id="element-4021" data-mp="3f5s1alzjs0h6">
                    <span className="font-semibold" data-id="element-4022" data-mp="3f6ba9d77zej2">Back:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.back}
                  </div>
                  <div data-id="element-4023" data-mp="3o4vjlnqw4bum">
                    <span className="font-semibold" data-id="element-4024" data-mp="3o43mu9lk482r">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neurologicExam}
                  </div>
                  <div data-id="element-4025" data-mp="1xf616uslntq">
                    <span className="font-semibold" data-id="element-4026" data-mp="1xyezy2gt1vm">Feeding:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-4027" data-mp="23qmj09mxntcg">
                  <span className="font-semibold" data-id="element-4028" data-mp="23ro5rbdba4a0">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-4029" data-mp="kw06w30q5m55">
                    {selectedFile.postNatalOneWeek?.infant.additionalNotes}
                  </p>
                </div>
              </div>

              {/* POSTNATAL AT SIX WEEKS */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-4030" data-mp="73kcfqlw2wfu">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-4031" data-mp="71h9y73jvau8">
                  Postnatal at Six Weeks
                </h3>
                <div className="text-sm mb-3" data-id="element-4032" data-mp="zrpxkeguung7">
                  <span className="font-semibold" data-id="element-4033" data-mp="zpv94q6dkd7d">Date:</span>{' '}
                  {selectedFile.postNatalSixWeeks?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-4034" data-mp="pul8o6vmkdvp">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-4035" data-mp="3fjwxmkgv0zsz">
                  <div data-id="element-4036" data-mp="3ajrc7m16o9jc">
                    <span className="font-semibold" data-id="element-4037" data-mp="3aig08yxl3vh5">General Well Being:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-4038" data-mp="wfq4zmq61uta">
                    <span className="font-semibold" data-id="element-4039" data-mp="wfgl8gvpsjw1">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.temp}
                  </div>
                  <div data-id="element-4040" data-mp="nn6bt1sn8ifu">
                    <span className="font-semibold" data-id="element-4041" data-mp="nllcyzzaqdc6">Pulse:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.pulse}
                  </div>
                  <div data-id="element-4042" data-mp="dnntepsdt4pw">
                    <span className="font-semibold" data-id="element-4043" data-mp="dn5lux09r42o">BP:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.bp}
                  </div>
                  <div data-id="element-4044" data-mp="4nilmeja04b7">
                    <span className="font-semibold" data-id="element-4045" data-mp="4o0ohu0g1gs3">Urine:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.urine}
                  </div>
                  <div data-id="element-4046" data-mp="2310ywic8cysl">
                    <span className="font-semibold" data-id="element-4047" data-mp="1z1tc0rfir6j3">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.head}
                  </div>
                  <div data-id="element-4048" data-mp="3gywobis4pc5i">
                    <span className="font-semibold" data-id="element-4049" data-mp="3gxc1ygfywziq">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.eyes}
                  </div>
                  <div data-id="element-4050" data-mp="3qq9jaj984ifr">
                    <span className="font-semibold" data-id="element-4051" data-mp="3qr2p9i0cz3wu">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ears}
                  </div>
                  <div data-id="element-4052" data-mp="2y1cbja2n2419">
                    <span className="font-semibold" data-id="element-4053" data-mp="2y1vki1ab9i35">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.mouth}
                  </div>
                  <div data-id="element-4054" data-mp="370pls4yl6wn1">
                    <span className="font-semibold" data-id="element-4055" data-mp="3707iwphf5k65">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.neck}
                  </div>
                  <div data-id="element-4056" data-mp="mb065pq276ta">
                    <span className="font-semibold" data-id="element-4057" data-mp="m9q6iw3k5qkj">Breasts:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.breasts}
                  </div>
                  <div data-id="element-4058" data-mp="2ncze20nnyzk9">
                    <span className="font-semibold" data-id="element-4059" data-mp="2nc7vbqf60wbe">Ampills:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ampills}
                  </div>
                  <div data-id="element-4060" data-mp="2jdqj4sgw0vn4">
                    <span className="font-semibold" data-id="element-4061" data-mp="2xboz22aux04q">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-4062" data-mp="24mhp875yco46">
                    <span className="font-semibold" data-id="element-4063" data-mp="24lyepbimt206">Groin:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.groin}
                  </div>
                  <div data-id="element-4064" data-mp="1zlk87xwoi049">
                    <span className="font-semibold" data-id="element-4065" data-mp="2ec1atkl1uw2q">Perineum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.perineum}
                  </div>
                  <div data-id="element-4066" data-mp="29jhuwgyw3our">
                    <span className="font-semibold" data-id="element-4067" data-mp="1lw07syqkley4">Legs:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.legs}
                  </div>
                  <div data-id="element-4068" data-mp="1gwduyqenm0pu">
                    <span className="font-semibold" data-id="element-4069" data-mp="1gv2t4w4rr31d">Anus:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.anus}
                  </div>
                  <div data-id="element-4070" data-mp="16d2ig23lerh">
                    <span className="font-semibold" data-id="element-4071" data-mp="16wbh79rsstd">Lochia:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-4072" data-mp="3nlpbyggo5v09">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-4073" data-mp="k4gy07p2bfat">
                  <div data-id="element-4074" data-mp="k35rw1859vgj">
                    <span className="font-semibold" data-id="element-4075" data-mp="k2cuhqv2sib2">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-4076" data-mp="64ejmvfyr40b">
                      {selectedFile.postNatalSixWeeks?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-4077" data-mp="2qtc0xs3iei03">
                    <span className="font-semibold" data-id="element-4078" data-mp="2qwzwsxwffghz">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-4079" data-mp="2vvktwogdtk27">
                      {selectedFile.postNatalSixWeeks?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-4080" data-mp="36c4zcat0p7bp">
                    <span className="font-semibold" data-id="element-4081" data-mp="36atsu386tlgw">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-4082" data-mp="31ed3j5p95128">
                      {selectedFile.postNatalSixWeeks?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-4083" data-mp="3u0eq7r538l9p">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-4084" data-mp="3l1b6cky3k1u5">
                  <div data-id="element-4085" data-mp="3g2q5qkexng56">
                    <span className="font-semibold" data-id="element-4086" data-mp="3g39epbmluu72">Infant:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.name}
                  </div>
                  <div data-id="element-4087" data-mp="29kkxeu2agvwu">
                    <span className="font-semibold" data-id="element-4088" data-mp="24nudtwr9zi6o">Weight:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.weight}
                  </div>
                  <div data-id="element-4089" data-mp="k7d2vnw1kkf3">
                    <span className="font-semibold" data-id="element-4090" data-mp="p4mt35r9903z">Date:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-4091" data-mp="t43zq2if43aq">
                  <div data-id="element-4092" data-mp="xyhx2agn4ki1">
                    <span className="font-semibold" data-id="element-4093" data-mp="xyrq66wzf7ry">Head Circumference:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.headCircumference}
                  </div>
                  <div data-id="element-4094" data-mp="17t9y5o9flphk">
                    <span className="font-semibold" data-id="element-4095" data-mp="1cu7mkil1bfri">Length:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.length}
                  </div>
                  <div data-id="element-4096" data-mp="1gv9f6gkg4tki">
                    <span className="font-semibold" data-id="element-4097" data-mp="1gtgi9szmxnpp">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.temp}
                  </div>
                  <div data-id="element-4098" data-mp="3inxj6s4wzl1c">
                    <span className="font-semibold" data-id="element-4099" data-mp="3info3ytzncv0">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.head}
                  </div>
                  <div data-id="element-4100" data-mp="kue37at9x4gq">
                    <span className="font-semibold" data-id="element-4101" data-mp="kv5l5ity73oj">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.eyes}
                  </div>
                  <div data-id="element-4102" data-mp="ur8zdn22qsru">
                    <span className="font-semibold" data-id="element-4103" data-mp="uqpim9o5uafe">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.ears}
                  </div>
                  <div data-id="element-4104" data-mp="13sgjn17r9t2x">
                    <span className="font-semibold" data-id="element-4105" data-mp="13t8h6hkwy0vu">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.mouth}
                  </div>
                  <div data-id="element-4106" data-mp="1ij5we80v7g8w">
                    <span className="font-semibold" data-id="element-4107" data-mp="1dhqc4i6ihdrk">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neck}
                  </div>
                  <div data-id="element-4108" data-mp="1riaz7qjglvqv">
                    <span className="font-semibold" data-id="element-4109" data-mp="1miybz4ooemnr">Chest:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.chest}
                  </div>
                  <div data-id="element-4110" data-mp="2659k12nt9rwm">
                    <span className="font-semibold" data-id="element-4111" data-mp="262x6d1p363pd">Abdomen:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.abdomen}
                  </div>
                  <div data-id="element-4112" data-mp="1x3a30acsqnto">
                    <span className="font-semibold" data-id="element-4113" data-mp="1x64o843mwco5">Cord:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.cord}
                  </div>
                  <div data-id="element-4114" data-mp="1nd7bkelbphxg">
                    <span className="font-semibold" data-id="element-4115" data-mp="1ncnq4nwgsbeo">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-4116" data-mp="1dhdq25pmme3j">
                    <span className="font-semibold" data-id="element-4117" data-mp="1dhvsxl6snqkf">Extremities:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.extremities}
                  </div>
                  <div data-id="element-4118" data-mp="14gpbn9wbmbq4">
                    <span className="font-semibold" data-id="element-4119" data-mp="14g62oionexo8">Back:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.back}
                  </div>
                  <div data-id="element-4120" data-mp="1owqtdwiprb4k">
                    <span className="font-semibold" data-id="element-4121" data-mp="1ovywmiddr7cp">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neurologicExam}
                  </div>
                  <div data-id="element-4122" data-mp="1ex9p9jj415ap">
                    <span className="font-semibold" data-id="element-4123" data-mp="1ewq8i6574my9">Feeding:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-4124" data-mp="15yegq0slfj48">
                  <span className="font-semibold" data-id="element-4125" data-mp="15zfr031sc1kw">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-4126" data-mp="115lcde11ecso">
                    {selectedFile.postNatalSixWeeks?.infant.additionalNotes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};